import React, { useState, useEffect, useCallback } from "react";
import { LayoutDashboard, Users, BookOpen, Mail, Video, Bot, TrendingUp, Plus, Trash2 } from "lucide-react";
import { chatbotConfig } from "../mock/admin";
import { useAuth } from "../context/AuthContext";
import { api, useFetch } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const tabs = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "content", label: "Content", icon: BookOpen },
  { id: "newsletters", label: "Newsletters", icon: Mail },
  { id: "webinars", label: "Webinars", icon: Video },
  { id: "chatbot", label: "Chatbot · Sam", icon: Bot }
];

function StatCard({ label, value, accent }) {
  return (
    <div className="rp-card bg-white border border-[#E7E0D2] rounded-2xl p-5">
      <p className="font-mono-label text-[10px] text-[#1A1914]/40">{label}</p>
      <p className="mt-1 font-display font-bold text-[30px]" style={{ color: accent || "#1A1914" }}>{value}</p>
    </div>
  );
}

function ContentTab({ toast }) {
  const [frameworks, setFrameworks] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", category: "Enterprise Risk" });
  const [adding, setAdding] = useState(false);

  const refetch = useCallback(() => { api.get("/frameworks").then((r) => setFrameworks(r.data)).catch(() => {}); }, []);
  useEffect(() => { refetch(); }, [refetch]);

  const addFramework = async (e) => {
    e.preventDefault();
    try {
      await api.post("/frameworks", { ...form, description: "Draft entry — edit to complete.", full: form.name });
      toast({ title: "Framework created", description: `${form.name} added to the library.` });
      setForm({ name: "", slug: "", category: "Enterprise Risk" });
      setAdding(false);
      refetch();
    } catch (err) {
      toast({ title: "Could not create", description: err.response?.data?.detail || "Are you logged in?", variant: "destructive" });
    }
  };

  const del = async (slug, name) => {
    try {
      await api.delete(`/frameworks/${slug}`);
      toast({ title: "Deleted", description: `${name} removed.` });
      refetch();
    } catch (err) {
      toast({ title: "Could not delete", description: err.response?.data?.detail || "Are you logged in?", variant: "destructive" });
    }
  };

  return (
    <div data-testid="admin-content">
      <div className="bg-white border border-[#E7E0D2] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono-label text-[10px] text-[#1A1914]/40">Framework entries ({frameworks.length})</p>
          <button onClick={() => setAdding(!adding)} data-testid="admin-add-framework" className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold text-white bg-[#3F4CE0] hover:bg-[#3341C6] rounded-lg rp-btn"><Plus size={14} /> Add framework</button>
        </div>
        {adding && (
          <form onSubmit={addFramework} className="mb-4 p-4 bg-[#FAF7F1] border border-[#E7E0D2] rounded-xl flex flex-wrap gap-2 items-end" data-testid="admin-framework-form">
            <div className="flex-1 min-w-[160px]">
              <label className="font-mono-label text-[9px] text-[#1A1914]/40">Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="framework-name-input" className="w-full mt-1 px-3 py-2 text-[13px] border border-[#E7E0D2] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#3F4CE0]/40" />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="font-mono-label text-[9px] text-[#1A1914]/40">Slug</label>
              <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="framework-slug-input" className="w-full mt-1 px-3 py-2 text-[13px] border border-[#E7E0D2] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#3F4CE0]/40" />
            </div>
            <button type="submit" data-testid="framework-save-btn" className="px-4 py-2 text-[13px] font-semibold text-white bg-[#12B76A] hover:bg-[#0E9355] rounded-lg rp-btn">Save</button>
          </form>
        )}
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {frameworks.map((f) => (
            <div key={f.slug} className="flex items-center justify-between px-4 py-2.5 bg-[#FAF7F1] border border-[#E7E0D2] rounded-lg">
              <span className="text-[13.5px] font-medium">{f.name} <span className="text-[#1A1914]/40 text-[12px]">· {f.category}</span></span>
              <button onClick={() => del(f.slug, f.name)} data-testid={`delete-framework-${f.slug}`} className="p-1.5 rounded-lg hover:bg-white text-[#DC2626]/70" aria-label="Delete"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const { toast } = useToast();
  const { user, loading: authLoading, login } = useAuth();
  const { data: stats } = useFetch(user ? "/admin/stats" : "/");
  const { data: usersData } = useFetch(user ? "/admin/users" : "/");
  const { data: newsData } = useFetch(user ? "/admin/newsletters" : "/");
  const { data: webinars } = useFetch("/webinars");

  if (authLoading) return <main className="min-h-[60vh] flex items-center justify-center" data-testid="admin-page"><div className="w-10 h-10 rounded-full border-[3px] border-[#3F4CE0] border-t-transparent animate-spin" /></main>;

  if (!user) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center px-5" data-testid="admin-page">
        <div className="text-center max-w-md">
          <p className="font-mono-label text-[11px] text-[#3F4CE0]">Admin console</p>
          <h1 className="mt-2 font-display font-bold text-[32px]">Sign in required.</h1>
          <p className="mt-2 text-[14.5px] text-[#1A1914]/60">The admin console is protected. Sign in with Google to manage content, users and Sam's configuration.</p>
          <button onClick={login} data-testid="admin-login-btn" className="mt-6 px-6 py-3 text-[14.5px] font-semibold text-white bg-[#3F4CE0] hover:bg-[#3341C6] rounded-xl rp-btn">Continue with Google</button>
        </div>
      </main>
    );
  }

  const adminUsers = usersData || [];
  const campaigns = newsData?.campaigns || [];
  const subscribers = newsData?.subscribers || [];

  return (
    <main className="max-w-[1320px] mx-auto px-5 lg:px-8 py-10" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-mono-label text-[11px] text-[#3F4CE0]">Admin console</p>
          <h1 className="mt-1 font-display font-bold text-[32px] lg:text-[40px] tracking-tight">Websites Admin</h1>
        </div>
        <span className="font-mono-label text-[10px] px-3 py-1.5 rounded-full bg-[#12B76A]/10 border border-[#12B76A]/25 text-[#0E9355]">Live · {user.email}</span>
      </div>
      <div className="mt-8 flex flex-col lg:flex-row gap-8">
        <nav className="lg:w-56 shrink-0 flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2.5 px-4 py-2.5 text-[14px] font-medium rounded-xl whitespace-nowrap rp-btn ${tab === t.id ? "bg-[#1A1914] text-white" : "text-[#1A1914]/70 hover:bg-[#F3EEE4]"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="flex-1 min-w-0">
          {tab === "overview" && (
            <div data-testid="admin-overview">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard label="Registered users" value={stats?.users ?? "—"} />
                <StatCard label="Newsletter subs" value={stats?.newsletterSubs ?? "—"} accent="#3F4CE0" />
                <StatCard label="Chat sessions" value={stats?.chatSessions ?? "—"} accent="#E8590C" />
                <StatCard label="Glossary terms" value={stats?.glossaryTerms ?? "—"} accent="#0E9355" />
              </div>
              <div className="mt-6 grid lg:grid-cols-2 gap-4">
                <div className="bg-white border border-[#E7E0D2] rounded-2xl p-6">
                  <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-4 flex items-center gap-1.5"><TrendingUp size={12} /> Weekly signups</p>
                  <div className="flex items-end gap-2 h-36">
                    {(stats?.weeklySignups || []).map((v, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                        <div className="w-full rounded-t-lg bg-[#3F4CE0] transition-all duration-700 hover:bg-[#3341C6]" style={{ height: `${(v / 90) * 100}%` }} title={`${v} signups`} />
                        <span className="font-mono-label text-[9px] text-[#1A1914]/40">{["M", "T", "W", "T", "F", "S", "S"][i]}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white border border-[#E7E0D2] rounded-2xl p-6">
                  <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-4">Top pages (7 days)</p>
                  <div className="space-y-3">
                    {(stats?.topPages || []).map((p) => (
                      <div key={p.path} className="flex items-center justify-between">
                        <code className="text-[12.5px] text-[#3F4CE0]">{p.path}</code>
                        <span className="font-mono-label text-[11px] text-[#1A1914]/55">{p.views.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {tab === "users" && (
            <div className="overflow-x-auto rounded-2xl border border-[#E7E0D2] bg-white" data-testid="admin-users">
              <table className="w-full text-left min-w-[640px]">
                <thead><tr className="border-b border-[#E7E0D2] bg-[#F3EEE4]">{["Name", "Email", "Role", "Plan", "Joined"].map((h, i) => <th key={i} className="px-5 py-3.5 font-mono-label text-[10px] text-[#1A1914]/50">{h}</th>)}</tr></thead>
                <tbody>
                  {adminUsers.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-[13.5px] text-[#1A1914]/50">No registered users yet — Google sign-ins will appear here.</td></tr>}
                  {adminUsers.map((u) => (
                    <tr key={u.user_id} className="border-b border-[#E7E0D2]/60 last:border-0 hover:bg-[#FAF7F1] transition-colors">
                      <td className="px-5 py-3.5 font-semibold text-[13.5px]">{u.name || "—"}</td>
                      <td className="px-5 py-3.5 text-[13px] text-[#1A1914]/60">{u.email}</td>
                      <td className="px-5 py-3.5"><span className={`font-mono-label text-[9.5px] px-2 py-0.5 rounded-full border ${u.role === "admin" ? "bg-[#E5484D]/10 border-[#E5484D]/25 text-[#DC2626]" : "bg-[#F3EEE4] border-[#E7E0D2] text-[#1A1914]/55"}`}>{u.role || "user"}</span></td>
                      <td className="px-5 py-3.5 text-[13px]">{u.plan || "Free"}</td>
                      <td className="px-5 py-3.5 text-[12.5px] text-[#1A1914]/50">{(u.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tab === "content" && <ContentTab toast={toast} />}
          {tab === "newsletters" && (
            <div className="space-y-4" data-testid="admin-newsletters">
              <div className="bg-white border border-[#E7E0D2] rounded-2xl overflow-hidden">
                <p className="px-6 py-4 border-b border-[#E7E0D2] font-mono-label text-[10px] text-[#1A1914]/40">Campaigns</p>
                <table className="w-full text-left">
                  <thead><tr className="border-b border-[#E7E0D2] bg-[#F3EEE4]">{["Subject", "Status", "Date", "Opens", "Clicks"].map((h) => <th key={h} className="px-6 py-3 font-mono-label text-[10px] text-[#1A1914]/50">{h}</th>)}</tr></thead>
                  <tbody>
                    {campaigns.map((n) => (
                      <tr key={n.id} className="border-b border-[#E7E0D2]/60 last:border-0">
                        <td className="px-6 py-4 text-[13.5px] font-medium">{n.subject}</td>
                        <td className="px-6 py-4"><span className={`font-mono-label text-[9.5px] px-2 py-0.5 rounded-full border ${n.status === "Sent" ? "bg-[#12B76A]/10 border-[#12B76A]/25 text-[#0E9355]" : "bg-[#F3EEE4] border-[#E7E0D2] text-[#1A1914]/55"}`}>{n.status}</span></td>
                        <td className="px-6 py-4 text-[12.5px] text-[#1A1914]/55">{n.date}</td>
                        <td className="px-6 py-4 text-[13px]">{n.opens}</td>
                        <td className="px-6 py-4 text-[13px]">{n.clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-white border border-[#E7E0D2] rounded-2xl p-6">
                <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-3">Subscribers ({subscribers.length})</p>
                {subscribers.length === 0 ? <p className="text-[13.5px] text-[#1A1914]/50">No subscribers yet — the homepage form feeds this list.</p> : (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                    {subscribers.map((s) => (
                      <div key={s.id} className="flex justify-between text-[13px] px-3.5 py-2 bg-[#FAF7F1] border border-[#E7E0D2] rounded-lg">
                        <span className="font-medium">{s.email}</span>
                        <span className="text-[#1A1914]/45">{(s.created_at || "").slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {tab === "webinars" && (
            <div className="space-y-3" data-testid="admin-webinars">
              {(webinars || []).map((w) => (
                <div key={w.id} className="rp-card bg-white border border-[#E7E0D2] rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-semibold text-[15px]">{w.title}</p>
                    <p className="mt-1 text-[13px] text-[#1A1914]/55">{w.guest} · {w.date} · {w.registrations} registered</p>
                  </div>
                  <span className={`font-mono-label text-[9.5px] px-2.5 py-1 rounded-full border ${w.status === "Upcoming" ? "bg-[#3F4CE0]/5 border-[#3F4CE0]/20 text-[#3F4CE0]" : "bg-[#12B76A]/10 border-[#12B76A]/25 text-[#0E9355]"}`}>{w.status}</span>
                </div>
              ))}
            </div>
          )}
          {tab === "chatbot" && (
            <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-chatbot">
              <div className="bg-white border border-[#E7E0D2] rounded-2xl p-6">
                <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-4">Sam — configuration</p>
                <div className="space-y-3">
                  {[["Assistant name", chatbotConfig.name], ["Model", "claude-sonnet-4-6"], ["Provider", "Anthropic via Emergent LLM"], ["Streaming", "SSE — enabled"]].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between px-4 py-3 bg-[#FAF7F1] border border-[#E7E0D2] rounded-lg">
                      <span className="text-[13px] text-[#1A1914]/60">{k}</span><code className="text-[12.5px] font-semibold">{v}</code>
                    </div>
                  ))}
                </div>
                <p className="mt-4 font-mono-label text-[10px] text-[#1A1914]/40">System prompt (preview)</p>
                <p className="mt-2 text-[12.5px] leading-relaxed text-[#1A1914]/60 bg-[#FAF7F1] border border-[#E7E0D2] rounded-lg p-4 font-mono">{chatbotConfig.systemPromptPreview}</p>
              </div>
              <div className="bg-white border border-[#E7E0D2] rounded-2xl p-6">
                <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-4">Guardrails</p>
                <ul className="space-y-2.5">
                  {chatbotConfig.guardrails.map((g) => (
                    <li key={g} className="text-[13.5px] flex gap-2.5 px-4 py-3 bg-[#12B76A]/5 border border-[#12B76A]/20 rounded-lg"><Bot size={15} className="text-[#0E9355] shrink-0 mt-0.5" />{g}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
